import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, ArrowLeft, Tag } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }

    fetchCategories();
  }, [navigate]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error carregant categories:', error);
      toast.error('Error carregant categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) {
      toast.error('El nom de la categoria és obligatori');
      return;
    }

    try {
      await axios.post(`${API}/categories`, { name: categoryName });
      toast.success('Categoria creada');
      setCategoryName('');
      setShowAddForm(false);
      fetchCategories();
    } catch (error) {
      console.error('Error creant categoria:', error);
      toast.error('Error creant la categoria');
    }
  };

  const handleEditCategory = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) {
      toast.error('El nom de la categoria és obligatori');
      return;
    }

    try {
      await axios.put(`${API}/categories/${editingCategory.id}`, { name: categoryName });
      toast.success('Categoria actualitzada');
      setCategoryName('');
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Error actualitzant categoria:', error);
      toast.error('Error actualitzant la categoria');
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Segur que vols eliminar aquesta categoria?')) {
      return;
    }

    try {
      await axios.delete(`${API}/categories/${categoryId}`);
      toast.success('Categoria eliminada');
      fetchCategories();
    } catch (error) {
      console.error('Error eliminant categoria:', error);
      const message = error.response?.data?.detail || 'Error eliminant la categoria';
      toast.error(message);
    }
  };

  const startEdit = (category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setShowAddForm(false);
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setCategoryName('');
  };

  const startAdd = () => {
    setShowAddForm(true);
    setEditingCategory(null);
    setCategoryName('');
  };

  return (
    <div data-testid="admin-categories-page" className="min-h-screen bg-muted/30 px-6 py-12">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" />
            Tornar al panell
          </Link>
          <div className="flex items-center justify-between">
            <h1 className="font-heading text-4xl font-medium text-foreground">
              Categories
            </h1>
            {!showAddForm && !editingCategory && (
              <Button 
                onClick={startAdd}
                data-testid="add-category-button" 
                className="rounded-xl"
              >
                <Plus className="h-5 w-5 mr-2" />
                Afegir Categoria
              </Button>
            )}
          </div>
        </div>

        {/* Add/Edit Form */}
        {(showAddForm || editingCategory) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-2xl shadow-sm border border-border/50 p-6 mb-6"
          >
            <h2 className="font-heading text-xl font-medium text-foreground mb-4">
              {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
            </h2>
            <form onSubmit={editingCategory ? handleEditCategory : handleAddCategory} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-base mb-2">Nom de la Categoria</Label>
                <Input
                  id="name"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  data-testid="category-name-input"
                  className="h-12 rounded-xl"
                  placeholder="Ex: Decoració, Complements..."
                  required
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  type="submit" 
                  data-testid="save-category-button"
                  className="rounded-xl"
                >
                  {editingCategory ? 'Actualitzar' : 'Crear'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowAddForm(false);
                    cancelEdit();
                  }}
                  className="rounded-xl"
                >
                  Cancel·lar
                </Button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Categories List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Carregant categories...</p>
          </div>
        ) : categories.length === 0 ? (
          <div data-testid="no-categories" className="text-center py-12">
            <Tag className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-6">No hi ha categories encara</p>
            <Button onClick={startAdd}>Afegir la primera categoria</Button>
          </div>
        ) : (
          <div data-testid="categories-list" className="space-y-3">
            {categories.map((category) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                data-testid={`category-item-${category.id}`}
                className="bg-card rounded-2xl shadow-sm border border-border/50 p-6 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center">
                    <Tag className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-medium text-lg text-foreground">
                      {category.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      ID: {category.id}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => startEdit(category)}
                    data-testid={`edit-category-${category.id}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => handleDelete(category.id)}
                    data-testid={`delete-category-${category.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCategories;